/**
 * ToolLayout — Shell wrapper for promoted tool pages.
 * Provides: sidebar + header + content area.
 * Used by ReportBuilderPage, CopyBuilderPage, etc.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ReactNode } from 'react';
import AppHeader from './AppHeader';
import Sidebar from './Sidebar';
import Settings from './Settings';
import type { Theme, Skin } from './Settings';
import { loadBookmarks } from './Bookmarks';
import { loadHighlights } from '../highlights';

interface ToolLayoutProps {
  children: ReactNode;
}

export default function ToolLayout({ children }: ToolLayoutProps) {
  const isAdminUser = localStorage.getItem('standards-user-role') === 'admin';
  const navigate = useNavigate();
  const [showSettings, setShowSettings] = useState(false);

  const [theme, setTheme] = useState<Theme>(() => {
    const stored = localStorage.getItem('ch-theme') as Theme | null;
    return stored || 'dark';
  });
  const [skin, setSkin] = useState<Skin>(() => {
    const stored = localStorage.getItem('ch-skin') as Skin | null;
    return stored || 'synthesis';
  });

  const researchBadge = loadBookmarks().length + loadHighlights().length;

  const hasCorpusUpdates = (() => {
    try {
      const lastSeen = localStorage.getItem('ch-corpus-updates-seen');
      return !lastSeen || new Date(lastSeen) < new Date('2026-06-13');
    } catch { return false; }
  })();

  const handleOpenHelp = () => {
    const guide = document.querySelector('a[href="/user-guide.html"]') as HTMLAnchorElement | null;
    if (guide) guide.click();
    else window.open('/user-guide.html', '_blank');
  };

  const handleOpenResources = () => {
    // Resources dropdown — no easy way to trigger from here; navigate to chat
    // where the full toolbar with ResourceLinks is available
    // For now, open a simple fallback
    window.open('https://www.standards.org.au/', '_blank');
  };

  return (
    <div className="app">
      <Sidebar
        isAdminUser={isAdminUser}
        onOpenSettings={() => setShowSettings(true)}
        onOpenHelp={handleOpenHelp}
        onOpenResources={handleOpenResources}
        onOpenHistory={() => navigate('/')}
        onOpenShortcuts={() => { /* shortcuts modal not available in tool layout */ }}
        researchBadge={researchBadge}
        hasCorpusUpdates={hasCorpusUpdates}
      />
      <div className="app-content">
        <AppHeader isAdminUser={isAdminUser} />
        {children}
      </div>

      {/* Settings sidebar */}
      <Settings
        isOpen={showSettings}
        theme={theme}
        onThemeChange={(t) => { setTheme(t); }}
        skin={skin}
        onSkinChange={(s) => { setSkin(s); }}
        onClose={() => setShowSettings(false)}
        onPreferencesChange={() => {}}
      />
    </div>
  );
}
